import React from 'react';
import { useLocation } from 'react-router-dom';
import { BarChart3, Menu, X } from 'lucide-react';
import { useSidebar } from '../contexts/SidebarContext';
import { navGroupFor } from '../utils/navigation';
import { pageTipFor } from '../content/glossary';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import InfoTip from './InfoTip';
import BeforeBoundaryNotice from './BeforeBoundaryNotice';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Not one of the menu pages (the "not found" page): no group caption and no tip, whatever the address says. */
  plain?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, icon: Icon = BarChart3, plain = false }) => {
  const { pathname } = useLocation();
  useDocumentTitle(title);
  const group = plain ? undefined : navGroupFor(pathname);
  const pageTip = plain ? undefined : pageTipFor(pathname);
  // "Standards · How time is logged ..." tells a person which family of pages they are in.
  const subline = group ? (subtitle ? `${group.caption} · ${subtitle}` : group.caption) : subtitle;
  const { open: sidebarOpen, toggle: toggleSidebar } = useSidebar();

  return (
    <>
      <div className="bg-white border-b border-gray-200 h-16 px-4 sm:px-6 flex items-center">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle navigation"
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
            </button>
            <div className="flex items-center space-x-3">
              <Icon className="w-6 h-6 text-primary-600" />
              <div>
                <div className="flex items-center">
                  <h1 className="text-lg font-bold text-gray-900 leading-tight">{title}</h1>
                  {pageTip && <InfoTip id={pageTip} />}
                </div>
                {subline && <p className="text-xs text-gray-500">{subline}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!plain && <BeforeBoundaryNotice />}
    </>
  );
};

export default PageHeader;
